import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import type { DeliverableCriticalityDatum } from '../reportMockData';
import { DELIVERABLE_CRITICALITY_ORDER } from '../reportMockData';
import { chrome, chartFont, criticalityColors } from '../reportTheme';
import ChartTooltip from './ChartTooltip';

const axisTick = { fill: chrome.muted, fontSize: 12, fontFamily: chartFont };

interface StackedStatusBarProps {
  data: DeliverableCriticalityDatum[];
}

const StackedStatusBar: React.FC<StackedStatusBarProps> = ({ data }) => {
  const { t } = useTranslation('templates');

  const rows = data.map((d) => ({ label: d.label, ...d.counts }));
  const lastKey = DELIVERABLE_CRITICALITY_ORDER[DELIVERABLE_CRITICALITY_ORDER.length - 1];

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={rows}
        layout="vertical"
        margin={{ top: 4, right: 16, left: 8, bottom: 0 }}
        barCategoryGap="28%"
      >
        <CartesianGrid stroke={chrome.grid} horizontal={false} />
        <XAxis
          type="number"
          tick={axisTick}
          tickLine={false}
          axisLine={false}
          allowDecimals={false}
        />
        <YAxis
          type="category"
          dataKey="label"
          tick={axisTick}
          tickLine={false}
          axisLine={{ stroke: chrome.axis }}
          width={104}
        />
        <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(45,120,219,0.06)' }} />
        <Legend
          iconType="circle"
          iconSize={8}
          wrapperStyle={{ fontFamily: chartFont, fontSize: 12, color: chrome.textSecondary }}
        />
        {DELIVERABLE_CRITICALITY_ORDER.map((criticality) => (
          <Bar
            key={criticality}
            dataKey={criticality}
            name={t(`report.criticality.${criticality}`)}
            stackId="deliverables"
            fill={criticalityColors[criticality]}
            maxBarSize={22}
            radius={criticality === lastKey ? [0, 4, 4, 0] : undefined}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

export default StackedStatusBar;
